import React, { useCallback, useRef } from 'react';

import { Box, Button } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import MatchReplaceTestInput from '../../../formInputs/MatchReplaceTestInput/MatchReplaceTestInput';
import CallPropertyInput from '../../components/CallPropertyInput';
import NodeControlForm from '../NodeControlForm';

import MappingRuleInput from './MappingRuleInput';

import type { MappingRule, InputColor, NodeControlsProps } from '../../dataProvider/types';

export interface MappingType extends Record<string, unknown> {
  mappingRules?: MappingRule[];
  matchProperty?: string;
  setProperty?: string;
}

interface MappingControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: MappingType;
}

export default function MappingControls({ updateNodeData, data }: MappingControlsProps): React.ReactElement {
  const [mappingRules, setMappingRules] = React.useState<MappingRule[]>(data.mappingRules ?? []);
  const [matchProperty, setMatchProperty] = React.useState<string>(data.matchProperty ?? 'called_id_number');
  const [setProperty, setSetProperty] = React.useState<string>(data.setProperty ?? 'called_id_number');
  const [mappingRulesColors, setMappingRulesColors] = React.useState<Record<number, InputColor>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const newRuleInputRef = useRef<HTMLInputElement>(null);
  const translate = useTranslate();

  const onDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('MappingControls: onDataUpdate', { mappingRules, matchProperty, setProperty });
    updateNodeData({ mappingRules, matchProperty, setProperty });
  };

  const [lastAddedIndex, setLastAddedIndex] = React.useState<number | null>(null);

  const addMappingRule = (): void => {
    const lastMappingRule = mappingRules[mappingRules.length - 1];
    flowchartLogger.debug('MappingControls: addMappingRule', { lastMappingRule });
    if (mappingRules.length < 1 || lastMappingRule?.matchRule !== '' || lastMappingRule?.replaceRule !== '') {
      flowchartLogger.debug('Mapping rule added');
      setLastAddedIndex(mappingRules.length);
      setMappingRules([...mappingRules, { matchRule: '', replaceRule: '' }]);
    }
  };

  React.useEffect(() => {
    if (lastAddedIndex === null) return;
    scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
    newRuleInputRef.current?.focus();
    setLastAddedIndex(null);
  }, [lastAddedIndex]);

  const updateMappingRule = (index: number, mappingRule: MappingRule): void => {
    flowchartLogger.debug('MappingControls: updateMappingRule', { index, mappingRule });
    const updatedMappingRules = [...mappingRules];
    updatedMappingRules[index] = mappingRule;
    setMappingRules(updatedMappingRules);
  };

  const deleteMappingRule = (index: number): void => {
    flowchartLogger.debug('MappingControls: deleteMappingRule', { index });
    const updatedMappingRules = [...mappingRules];
    updatedMappingRules.splice(index, 1);
    setMappingRules(updatedMappingRules);
  };

  const onMatchPropertyChange = useCallback(
    (matchProperty: string) => {
      setMatchProperty(matchProperty);
    },
    [setMatchProperty]
  );

  const onSetPropertyChange = useCallback(
    (setProperty: string) => {
      setSetProperty(setProperty);
    },
    [setSetProperty]
  );

  const onTestResult = (matchedIndex: number | null): void => {
    if (matchedIndex !== null) {
      flowchartLogger.debug('MappingControls: test result matched', { matchedIndex });
      setMappingRulesColors({ [matchedIndex]: 'success' });
    } else {
      flowchartLogger.debug('MappingControls: test result not matched', { matchedIndex });
      // if no match, set all colors to error
      const newMatchRulesColors: Record<number, InputColor> = {};
      mappingRules.forEach((matchRule, index) => {
        newMatchRulesColors[index] = 'error';
      });
      setMappingRulesColors(newMatchRulesColors);
    }
    setTimeout(() => {
      setMappingRulesColors({});
    }, 3000);
  };

  const getColorByIndex = (index: number): InputColor => {
    if (index in mappingRulesColors) {
      const inputColor = mappingRulesColors[index];
      if (inputColor !== undefined) {
        flowchartLogger.debug('MappingControls: getColorByIndex', { index, inputColor });
        return inputColor;
      }
    }
    flowchartLogger.debug('MappingControls: getColorByIndex default value returned', { index });
    return 'primary';
  };

  React.useEffect(() => {
    flowchartLogger.debug('MappingControls: mappingRules changed', { mappingRules });
    updateNodeData({ mappingRules, matchProperty, setProperty });
  }, [mappingRules, matchProperty, setProperty, updateNodeData]);

  const isFormDisabled = React.useCallback(() => {
    const isDisabled =
      mappingRules.length < 1 ||
      mappingRules.some((mappingRule) => mappingRule.matchRule === '' || mappingRule.replaceRule === '');
    flowchartLogger.debug('MappingControls: isFormDisabled', { isDisabled });
    return isDisabled;
  }, [mappingRules]);

  return (
    <NodeControlForm onSubmit={onDataUpdate} isDisabled={isFormDisabled()} data={data} updateNodeData={updateNodeData}>
      <CallPropertyInput
        callProperty={matchProperty}
        label={translate('callflows.nodes.router.matchProperty')}
        customInputLabel={translate('callflows.nodes.router.customMatchProperty')}
        checkboxLabel={translate('callflows.nodes.router.useCustomMatchProperty')}
        onChange={onMatchPropertyChange}
      />
      <CallPropertyInput
        callProperty={setProperty}
        label={translate('callflows.nodes.mapping.setProperty')}
        customInputLabel={translate('callflows.nodes.mapping.customSetProperty')}
        checkboxLabel={translate('callflows.nodes.mapping.useCustomSetProperty')}
        onChange={onSetPropertyChange}
      />
      <span>{translate('callflows.nodes.router.matchRules')}</span>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          ref={scrollContainerRef}
          overflow='auto'
          display='grid'
          gridAutoRows='max-content'
          gap={1}
          // Padding top to keep the label of the first row visible
          pt={1}
          sx={{ minHeight: 0 }}
        >
          {mappingRules.map((mappingRule, index) => (
            <MappingRuleInput
              // Can be disabled as we are not reordering mappingrules:
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={index === lastAddedIndex ? newRuleInputRef : undefined}
              mappingRule={mappingRule}
              onChange={(updatedMappingRule: MappingRule) => {
                updateMappingRule(index, updatedMappingRule);
              }}
              onDelete={() => {
                deleteMappingRule(index);
              }}
              color={getColorByIndex(index)}
            />
          ))}
        </Box>
        <Button variant='outlined' onClick={addMappingRule}>
          {translate('callflows.nodes.mapping.addMappingRule')}
        </Button>
        <MatchReplaceTestInput
          label={translate('callflows.components.matchReplaceTestInput.testMatchReplaceLabel')}
          onTestResult={onTestResult}
          matchRules={mappingRules.map((mappingRule) => ({
            match_rule: mappingRule.matchRule,
            replace_rule: mappingRule.replaceRule,
          }))}
        />
      </Box>
    </NodeControlForm>
  );
}
